import React, { useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { DeleteIcon } from '@lemonade-hq/nuis';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { ReviewType, TextReview, VideoReview } from '../types';
import { TextPlayerContent } from './TextPlayerContent';
import { VideoPlayerContent } from './VideoPlayerContent';

const PlayerWrapper = styled.div<{ isActive: boolean }>`
  max-height: 800px;
  max-width: 90vw;
  height: 82vh;
  min-height: min(800px, 82vh);
  margin-block: max(-15px, -1.5vh);
  aspect-ratio: 4.5/8;
  scroll-snap-align: center;

  --limit: calc(1 / (var(--num-of-items) - 1));
  --clampedT: clamp(calc(var(--limit) * (-1 + var(--index))), var(--t), calc(var(--limit) * (1 + var(--index))));
  scale: calc(
    calc(((cos(pi * (var(--clampedT) * (var(--num-of-items) - 1) + var(--index))) + 1) / 2)) *
      calc(1 - var(--min-scale)) + var(--min-scale)
  );
`;

const FadeIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const StyledIcon = styled(DeleteIcon).attrs({ colorOverride: 'white' })`
  position: absolute;
  right: -40px;
  top: -5px;
  cursor: pointer;
  animation: ${FadeIn} 0.3s ease-in-out;
  border-radius: 50%;

  ${MEDIA_QUERIES.mobile`display: none;`}
`;

const StyledMobileIcon = styled(DeleteIcon).attrs({ colorOverride: '#7b7b7b' })`
  position: absolute;
  right: 1.4rem;
  top: 1.4rem;
  cursor: pointer;
  animation: ${FadeIn} 0.3s ease-in-out;
  border-radius: 50%;
  background-color: white;
  z-index: 2;

  ${MEDIA_QUERIES.tabletAndDesktop`display: none;`}
`;

interface PlayerDialogProps {
  review: VideoReview | TextReview;
  onClose: () => void;
  isActive: boolean;
  changeActiveReview: (reviewId: number) => void;
  index: number;
  isMuted: boolean;
  setIsMuted: (isMuted: boolean) => void;
  captionsOn: boolean;
  setCaptionsOn: (isMuted: boolean) => void;
  videosDisclosure?: string;
}

export const Player: React.FC<PlayerDialogProps> = ({
  review,
  onClose,
  isActive = false,
  changeActiveReview,
  index,
  isMuted,
  setIsMuted,
  captionsOn,
  setCaptionsOn,
  videosDisclosure,
}) => {
  const handleClick = useCallback((): void => {
    if (isActive || !review.id) return;

    changeActiveReview(review.id);
  }, [isActive, review.id, changeActiveReview]);

  const onEnd = useCallback(() => {
    if (!review.id) return;

    changeActiveReview(review.id + 1);
  }, [review.id, changeActiveReview]);

  return (
    <PlayerWrapper
      isActive={isActive}
      onClick={handleClick}
      id={`review-${review.id}`}
      style={{ '--index': index } as React.CSSProperties}
    >
      {isActive && (
        <>
          <StyledIcon onClick={onClose} width="40px" height="40px" />
          <StyledMobileIcon onClick={onClose} width="30px" height="30px" />
        </>
      )}
      {review.type === ReviewType.Text ? (
        <TextPlayerContent review={review} isActive={isActive} onEnd={onEnd} />
      ) : (
        <VideoPlayerContent
          review={review}
          isActive={isActive}
          onEnd={onEnd}
          isMuted={isMuted}
          setIsMuted={setIsMuted}
          captionsOn={captionsOn}
          setCaptionsOn={setCaptionsOn}
          videosDisclosure={videosDisclosure}
        />
      )}
    </PlayerWrapper>
  );
};
